<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { clinicalDrugsApi, type ClinicalDrug } from '@/api/clinical-drugs';
import { inventoryApi } from '@/api/inventory';
import { apiErrorText } from '@/utils/api-errors';

// Adds a medication to the inventory from the global catalog: the item takes
// the medication's generic name and unit, so nothing is typed twice.
const emit = defineEmits<{ close: [] }>();

const queryClient = useQueryClient();
const SEARCH_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 3;

const search = ref('');
const term = ref('');
const drug = ref<ClinicalDrug | null>(null);
const categoryId = ref<number | undefined>();
const error = ref('');

let searchTimer: ReturnType<typeof setTimeout> | undefined;
watch(search, (value) => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    term.value = value ?? '';
  }, SEARCH_DELAY_MS);
});
onBeforeUnmount(() => clearTimeout(searchTimer));

const { data: drugs, isFetching } = useQuery({
  queryKey: ['clinicalDrugs', term],
  queryFn: () => clinicalDrugsApi.search(term.value),
  enabled: computed(() => term.value.trim().length >= MIN_SEARCH_LENGTH),
  initialData: [],
});

const { data: categories } = useQuery({
  queryKey: ['inventoryCategories', 'active'],
  queryFn: () => inventoryApi.categories(),
  initialData: [],
});

watch(categories, (list) => {
  if (categoryId.value) return;

  categoryId.value = list.find((category) => category.name === 'Medicamentos')?.id;
}, { immediate: true });

const noDataText = computed(() => (
  (search.value ?? '').trim().length < MIN_SEARCH_LENGTH ? 'Escribe al menos 3 letras' : 'Sin resultados'
));

const consumptionText = computed(() => {
  if (!drug.value) return '';

  return drug.value.consumptionPolicy === 'per_container' ?
    'Se cuenta por envase: se descuenta uno al abrir un envase nuevo.' :
    `Se cuenta por ${drug.value.doseUnitLabel}: cada dosis administrada descuenta su cantidad.`;
});

const save = useMutation({
  mutationFn: () => inventoryApi.saveItem({
    name: drug.value!.displayName,
    clinicalDrugId: drug.value!.id,
    inventoryCategoryId: categoryId.value,
    tracking: 'quantity',
    unitOfMeasure: drug.value!.inventoryUnitOfMeasure!,
  }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['inventoryItems'] });
    queryClient.invalidateQueries({ queryKey: ['clinicalDrugs'] });
    emit('close');
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo agregar el medicamento.');
  },
});
</script>
<template>
  <v-card title="Agregar medicamento">
    <v-card-text class="pt-5">
      <v-autocomplete
        v-model="drug"
        v-model:search="search"
        :items="drugs"
        :loading="isFetching"
        item-title="displayName"
        :item-props="(item: ClinicalDrug) => ({ disabled: !!item.inventoryItemId })"
        return-object
        no-filter
        label="Medicamento"
        placeholder="Nombre, marca o principio activo"
        hide-details
        variant="outlined"
        density="compact"
        :no-data-text="noDataText"
      >
        <template #item="{ props: itemProps, item }">
          <v-list-item
            v-bind="itemProps"
            :subtitle="item.raw.inventoryItemId ? 'Ya está en el inventario' : item.raw.doseFormLabel"
          />
        </template>
      </v-autocomplete>
      <p class="mt-1 mb-4 text-caption text-medium-emphasis">
        Del catálogo de medicamentos de Kuido. ¿No está? Pídelo a soporte.
      </p>
      <template v-if="drug">
        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          <div>Unidad de inventario: <b>{{ drug.humanInventoryUnitOfMeasure }}</b></div>
          <div>{{ consumptionText }}</div>
          <div v-if="drug.highAlert">
            Medicamento de alto riesgo.
          </div>
        </v-alert>
        <v-select
          v-model="categoryId"
          :items="categories"
          item-title="name"
          item-value="id"
          label="Categoría"
          variant="outlined"
          density="compact"
        />
      </template>
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        class="mt-3"
        :text="error"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="save.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="!drug || !categoryId"
        :loading="save.isPending.value"
        @click="save.mutate()"
      >
        Agregar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
